import React, { Fragment, useState, useEffect, useCallback } from 'react'
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  IconButton,
  Paper,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material'
import {
  Add,
  DeleteOutline,
  Edit,
  InfoOutlined,
  Save
} from '@mui/icons-material'
import { Modal } from '../../../components/_EOS/Modal'
import { SnackBar } from '../../../components/SnackBar/Snackbar'
import { Loading } from '../../../components/UI'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useModal } from '../../../hooks/useModal'
import { Status } from '../../../hooks/useFetchState'
import {
  getVasRuleDetailList,
  postVasRuledetail,
  deleteVasRuleDetail,
  patchVasRuledetail,
  vasRuleUpdateState
} from '../config/api'
import {
  VasRuleDetail,
  VasRuleDetailCreate,
  VasRuleDetailPatch
} from '../../../interfaces/VasRule/VasRule'
import { eTypeData } from '../../../enums/eTypeData'
import { ApiResponse } from '../../../typings/ApiResponse'

import { useStyles } from '../styles'

// --- TIPOS ---

interface DataCaptureCostumizationModalProps {
  vasRuleId: number
  onClose: () => void
  requestVasRule: () => void
  finalizeRef?: React.MutableRefObject<(() => void) | null>
}

interface ExtendedVasRuleDetail extends VasRuleDetail {
  isNew?: boolean
  isEditing?: boolean
}

// --- UTILIDADES ---

const adjustLengths = (
  fieldData: ExtendedVasRuleDetail,
  changedKey: keyof ExtendedVasRuleDetail,
  newValue: number
): ExtendedVasRuleDetail => {
  const updated = { ...fieldData, [changedKey]: newValue }

  if (changedKey === 'minLength' && newValue > updated.maxLength) {
    updated.maxLength = newValue
  }
  if (changedKey === 'maxLength' && newValue < updated.minLength) {
    updated.minLength = newValue
  }
  return updated
}

// --- CUSTOM HOOK ---

const useVasRuleLogic = (vasRuleId: number, requestVasRule: () => void) => {
  const { callApi, state } = useApi<ApiResponse<VasRuleDetail>>()
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const [fields, setFields] = useState<ExtendedVasRuleDetail[]>([])
  const [isSaving, setIsSaving] = useState(false)

  const fetchFields = useCallback(() => {
    if (!vasRuleId) return
    callApi(getVasRuleDetailList(vasRuleId, 0, 100)).catch((error) => {
      openSnackBar(error.message || 'Error al cargar datos', false)
    })
  }, [vasRuleId, callApi, openSnackBar])

  useEffect(() => {
    fetchFields()
  }, [vasRuleId])

  useEffect(() => {
    if (state.data?.items) {
      setFields((prevFields) => {
        const unsavedFields = prevFields.filter((f) => f.isNew)

        const fetchedFields = (state.data?.items || []).map((item) => ({
          ...item,
          isNew: false,
          isEditing: false
        }))

        return [...fetchedFields, ...unsavedFields]
      })
    }
  }, [state.data])

  // Handlers de estado local
  const addField = () => {
    const newId =
      fields.length > 0
        ? Math.max(...fields.map((f) => f.vasRuleDetailId)) + 1
        : 1
    setFields([
      ...fields,
      {
        vasRuleDetailId: newId,
        name: '',
        typeDataId: eTypeData.Alfanumérico,
        isDeleted: false,
        minLength: 1,
        maxLength: 50,
        vasRuleId,
        isUnique: false,
        isNew: true,
        isEditing: true
      }
    ])
  }

  const handleFieldChange = (
    id: number,
    field: keyof ExtendedVasRuleDetail,
    value: string | number | boolean
  ) => {
    const numValue = typeof value === 'string' ? parseInt(value) || 0 : value

    setFields((prevFields) =>
      prevFields.map((f) => {
        if (f.vasRuleDetailId !== id) return f

        if (field === 'minLength' || field === 'maxLength') {
          return adjustLengths(f, field, numValue as number)
        }

        if (field === 'isUnique') {
          return { ...f, isUnique: Number(value) === 1 || value === true }
        }

        return { ...f, [field]: value }
      })
    )
  }

  const startEditing = (id: number) => {
    setFields((prev) =>
      prev.map((f) =>
        f.vasRuleDetailId === id ? { ...f, isEditing: true } : f
      )
    )
  }

  const removeLocalField = (id: number) => {
    setFields((prev) => prev.filter((f) => f.vasRuleDetailId !== id))
  }

  // Operaciones API
  const createField = (field: ExtendedVasRuleDetail) => {
    const dto: VasRuleDetailCreate = {
      name: field.name,
      minLength: field.minLength,
      maxLength: field.maxLength,
      typeDataId: field.typeDataId,
      vasRuleId,
      isUnique: field.isUnique ?? false
    }
    setIsSaving(true)
    callApi(postVasRuledetail(dto))
      .then(() => {
        openSnackBar('Campo creado exitosamente', true)

        setFields((prev) =>
          prev.map((f) =>
            f.vasRuleDetailId === field.vasRuleDetailId
              ? {
                  ...f,
                  isNew: false,
                  isEditing: false
                }
              : f
          )
        )

        fetchFields()
      })
      .catch((err) => openSnackBar(err || 'Error al crear', false))
      .finally(() => setIsSaving(false))
  }

  const updateField = (field: ExtendedVasRuleDetail) => {
    const dto: VasRuleDetailPatch = {
      name: field.name,
      minLength: field.minLength,
      maxLength: field.maxLength,
      typeDataId: field.typeDataId,
      isUnique: field.isUnique ?? false
    }
    setIsSaving(true)
    callApi(patchVasRuledetail(field.vasRuleDetailId, dto))
      .then(() => {
        openSnackBar('Campo actualizado exitosamente', true)
        setFields((prev) =>
          prev.map((f) =>
            f.vasRuleDetailId === field.vasRuleDetailId
              ? { ...f, isEditing: false }
              : f
          )
        )
      })
      .catch((err) => openSnackBar(err || 'Error al actualizar', false))
      .finally(() => setIsSaving(false))
  }

  const deleteFieldApi = (id: number) => {
    return callApi(deleteVasRuleDetail(id))
      .then(() => {
        openSnackBar('Campo eliminado correctamente', true)
        removeLocalField(id)
      })
      .catch((err) => {
        openSnackBar(err || 'Error al eliminar', false)
        throw err
      })
  }

  const finalizeCustomization = (callback: () => void) => {
    callApi(vasRuleUpdateState(vasRuleId))
      .then(() => {
        openSnackBar('Campo(s) de captura finalizados exitosamente', true)
        setTimeout(callback, 1000)
        requestVasRule()
      })
      .catch((err) => openSnackBar(err || 'Error al finalizar', false))
  }

  return {
    fields,
    state,
    isSaving,
    snackbar: { isActive, message, isSuccess },
    actions: {
      addField,
      handleFieldChange,
      startEditing,
      createField,
      updateField,
      deleteFieldApi,
      removeLocalField,
      finalizeCustomization
    }
  }
}

// --- COMPONENTE PRINCIPAL (Vista) ---

export const DataCaptureCostumizationModal: React.FC<
  DataCaptureCostumizationModalProps
> = ({ vasRuleId, onClose, requestVasRule, finalizeRef }) => {
  const classes = useStyles()
  const deleteModal = useModal<VasRuleDetail>('Confirmar Eliminaci�n')
  const [separator] = useState(',')

  const { fields, state, isSaving, snackbar, actions } = useVasRuleLogic(
    vasRuleId,
    requestVasRule
  )

  useEffect(() => {
    if (finalizeRef) {
      finalizeRef.current = () => actions.finalizeCustomization(onClose)
    }
  }, [finalizeRef, onClose])

  const handleDeleteClick = (field: ExtendedVasRuleDetail) => {
    if (field.isNew) {
      actions.removeLocalField(field.vasRuleDetailId)
    } else {
      deleteModal.openModal('sm', field)
    }
  }

  const handleConfirmDelete = () => {
    const fieldToDelete = deleteModal.extendedProps as ExtendedVasRuleDetail
    if (!fieldToDelete) return

    actions.deleteFieldApi(fieldToDelete.vasRuleDetailId).then(() => {
      deleteModal.closeModal()
    })
  }

  return (
    <Fragment>
      <Paper elevation={1} sx={classes.rootPaper}>
        {/* Encabezado */}
        <Typography
          variant='body1'
          color='textSecondary'
          style={{ marginBottom: 24 }}
        >
          Define los campos que el operador deberá completar al aplicar este VAS
        </Typography>

        {/* Info Box */}
        <Box sx={classes.infoBox}>
          <InfoOutlined sx={classes.infoIcon} />
          <Typography variant='body2'>
            Si la personalización de la captura cuanta con 2 o mas valores a
            ingresar, se aplicará un separador al consultar dichos valores. Por
            ejemplo, para registrar múltiples seriales: "ABC123
            {separator}DEF456{separator}GHI789"
          </Typography>
        </Box>

        {/* Botonera Superior */}
        <Box sx={classes.controlsContainer}>
          <Typography
            variant='h6'
            style={{ fontSize: '1.1rem', fontWeight: 600 }}
          >
            Configuración de Campos
          </Typography>
          <Box display='flex' alignItems='center'>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<Add />}
              onClick={actions.addField}
              style={{ fontWeight: 600 }}
            >
              Agregar Campo
            </Button>
          </Box>
        </Box>

        {/* Tabla */}
        <Box style={{ position: 'relative' }}>
          {isSaving && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255,255,255,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: 4
              }}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={classes.tableContainer}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width='35%' sx={classes.tableHeadCell}>
                  Etiqueta
                </TableCell>
                <TableCell width='20%' sx={classes.tableHeadCell}>
                  Tipo de Dato
                </TableCell>
                <TableCell width='14%' sx={classes.tableHeadCell}>
                  Long. Mínima
                </TableCell>
                <TableCell width='14%' sx={classes.tableHeadCell}>
                  Long. Máxima
                </TableCell>
                <TableCell width='15%' sx={classes.tableHeadCell}>
                  <Box display='flex' alignItems='center' style={{ gap: 4 }}>
                    Valor único
                    <Tooltip title='Indica si el valor ingresado debe ser único por unidad. Si está activado, no se permitirá registrar el mismo valor dos veces.'>
                      <InfoOutlined style={{ fontSize: 14, color: '#888', cursor: 'default' }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  width='10%'
                  align='center'
                  sx={classes.tableHeadCell}
                >
                  Acciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((field) => {
                const isEditable = field.isNew || field.isEditing
                return (
                  <TableRow key={field.vasRuleDetailId}>
                    <TableCell sx={classes.tableBodyCell}>
                      <TextField
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={field.name}
                        placeholder='Nombre'
                        onChange={(e) =>
                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'name',
                            e.target.value
                          )
                        }
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell sx={classes.tableBodyCell}>
                      <Select
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={field.typeDataId}
                        onChange={(e) =>
                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'typeDataId',
                            e.target.value as number
                          )
                        }
                        disabled={!isEditable}
                      >
                        <MenuItem value={eTypeData.Alfanumérico}>
                          Alfanumérico
                        </MenuItem>
                        <MenuItem value={eTypeData.Numérico}>Numérico</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={classes.tableBodyCell}>
                      <TextField
                        type='number'
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={
                          String(field.minLength) === '0'
                            ? '1'
                            : field.minLength
                        }
                        onChange={(e) => {
                          const val = e.target.value
                          // Impedimos que el usuario escriba 0 manualmente
                          if (val !== '' && parseInt(val, 10) < 1) return

                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'minLength',
                            String(val)
                          )
                        }}
                        onBlur={(e) => {
                          let value = parseInt(e.target.value, 10)
                          const currentMax =
                            parseInt(String(field.maxLength), 10) || 100

                          if (isNaN(value) || value < 1) value = 1
                          if (value > currentMax) value = currentMax

                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'minLength',
                            String(value)
                          )
                        }}
                        inputProps={{ min: 1, max: field.maxLength || 100 }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell sx={classes.tableBodyCell}>
                      <TextField
                        type='number'
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={field.maxLength}
                        onChange={(e) => {
                          const val = e.target.value
                          if (val !== '' && parseInt(val, 10) < 1) return
                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'maxLength',
                            String(val)
                          )
                        }}
                        onBlur={(e) => {
                          let value = parseInt(e.target.value, 10)
                          const currentMin =
                            parseInt(String(field.minLength), 10) || 1

                          if (value > 100) value = 100

                          if (isNaN(value) || value < currentMin) {
                            value = currentMin
                          }

                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'maxLength',
                            String(value)
                          )

                          if (currentMin < 1) {
                            actions.handleFieldChange(
                              field.vasRuleDetailId,
                              'minLength',
                              '1'
                            )
                          }
                        }}
                        inputProps={{ min: field.minLength || 1, max: 100 }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    {/* Valor único */}
                    <TableCell sx={classes.tableBodyCell}>
                      <Select
                        fullWidth
                        variant='outlined'
                        size='small'
                        value={field.isUnique ? 1 : 0}
                        onChange={(e) =>
                          actions.handleFieldChange(
                            field.vasRuleDetailId,
                            'isUnique',
                            e.target.value
                          )
                        }
                        disabled={!isEditable}
                      >
                        <MenuItem value={1}>Sí</MenuItem>
                        <MenuItem value={0}>No</MenuItem>
                      </Select>
                    </TableCell>

                    {/* Botones de Acción */}
                    <TableCell align='center'>
                      <Box display='flex' alignItems='center' justifyContent='center'>
                      {field.isNew && (
                        <Tooltip title='Guardar nuevo campo'>
                          <IconButton
                            sx={classes.actionButton}
                            onClick={() => actions.createField(field)}
                          >
                            <Save />
                          </IconButton>
                        </Tooltip>
                      )}

                      {!field.isNew && !field.isEditing && (
                        <Tooltip title='Editar campo existente'>
                          <IconButton
                            sx={classes.actionButton}
                            onClick={() =>
                              actions.startEditing(field.vasRuleDetailId)
                            }
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      {!field.isNew && field.isEditing && (
                        <Tooltip title='Guardar cambios'>
                          <IconButton
                            sx={classes.actionButton}
                            color='primary'
                            onClick={() => actions.updateField(field)}
                          >
                            <Save />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton
                        size='small'
                        sx={classes.actionButton}
                        onClick={() => handleDeleteClick(field)}
                        title='Eliminar'
                      >
                        <DeleteOutline />
                      </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}

              {fields.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align='center' style={{ padding: 20 }}>
                    {state.status === Status.Loading ? (
                      <Loading />
                    ) : (
                      'No hay campos configurados.'
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>

        {/* Previsualización */}
        <Box sx={classes.previewBox}>
          <Typography
            variant='subtitle2'
            style={{ marginBottom: 16, fontWeight: 'bold', color: '#333' }}
          >
            Ejemplo de captura para unidad:
          </Typography>

          {fields.length === 0 ? (
            state.status === Status.Loading ? (
              <Loading />
            ) : (
              <Typography variant='body2' color='textSecondary'>
                No hay campos configurados. Haz click en 'Agregar Campo'.
              </Typography>
            )
          ) : (
            fields.map((field, index) => (
              <Box
                key={field.vasRuleDetailId}
                display='flex'
                alignItems='center'
                mb={1}
              >
                <Typography
                  variant='body2'
                  style={{ marginRight: 8, minWidth: 20 }}
                >
                  {index + 1}.
                </Typography>
                <Typography
                  variant='body2'
                  style={{ fontWeight: 600, marginRight: 8 }}
                >
                  {field.name || '(Sin nombre)'}:
                </Typography>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  style={{ fontStyle: 'italic' }}
                >
                  Escanear código{' '}
                  <span style={{ color: 'red', fontStyle: 'normal' }}>*</span>
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Paper>

      {/* Modales y Notificaciones */}
      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        loading={state.status === Status.Loading}
        successButton={{
          action: handleConfirmDelete,
          title: 'Confirmar'
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar'
        }}
      >
        <span>
          ¿Desea eliminar el campo de captura de la Regla VAS:{' '}
          <strong>{deleteModal.extendedProps?.name || '(Sin nombre)'}</strong>?
        </span>
      </Modal>

      <SnackBar
        message={snackbar.message}
        status={snackbar.isSuccess ? 'success' : 'error'}
        open={snackbar.isActive}
      />
    </Fragment>
  )
}

